import React, { useState } from 'react';
import axios from 'axios';
import {
  Monitor, Building2, User, Mail, Lock, Phone, CreditCard, FileText,
  Briefcase, Users, Shield, ChevronRight, ChevronLeft, CheckCircle2,
  Upload, Globe, DollarSign, PenTool, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ['Entity Type', 'Basic Info', 'Entity Details', 'Team Capacity', 'Skills & Tools', 'Experience', 'Documents', 'NDA Agreement', 'Review & Submit'];
const TOOLS_LIST = ['Blender', 'Maya', '3ds Max', 'Unreal Engine', 'Houdini', 'After Effects', 'Nuke', 'Substance Painter', 'ZBrush', 'Premiere Pro', 'DaVinci Resolve', 'Deadline', 'Shotgun/ftrack'];
const TEAM_ROLES = [
  { key: 'animators', label: 'Animators' }, { key: 'modelers_3d', label: '3D Modelers' },
  { key: 'rigging', label: 'Rigging Artists' }, { key: 'lighting', label: 'Lighting Artists' },
  { key: 'vfx', label: 'VFX Artists' }, { key: 'compositors', label: 'Compositors' },
  { key: 'editors', label: 'Editors' }, { key: 'sound_designers', label: 'Sound Designers' },
  { key: 'texture_artists', label: 'Texture Artists' }, { key: 'concept_artists', label: 'Concept Artists' },
];

export default function VendorRegisterPage() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    entity_type: '', full_name: '', company_name: '', email: '', password: '', confirm_password: '', phone: '',
    aadhar_number: '', pan_number: '', gst_number: '', cin_number: '', registered_address: '', pincode: '',
    authorized_person: '', authorized_person_pan: '',
    bank_account_name: '', bank_account_number: '', bank_ifsc: '', bank_name: '',
    team_capacity: {}, total_team_size: 0, peak_capacity: '',
    skills: '', tools: [],
    years_experience: '', past_projects: '', showreel_url: '', work_type_preference: 'per_shot',
    pricing_model: 'per_shot', min_project_size: '', payment_terms: 'milestone', advance_percentage: 0,
    portfolio_url: '', nda_signed: false, nda_signature: '',
  });

  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[\d+\-\s]{10,15}$/;
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
  const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z][Z][0-9A-Z]$/;

  const validate = () => {
    if (step === 1) {
      if (!form.company_name && !form.full_name) return 'Company / Full Name is required';
      if (!form.email) return 'Email is required';
      if (!emailRegex.test(form.email)) return 'Enter a valid email address (e.g., name@company.com)';
      if (!form.password || form.password.length < 6) return 'Password must be at least 6 characters';
      if (form.password !== form.confirm_password) return 'Passwords do not match';
      if (!form.phone) return 'Phone number is required';
      if (!phoneRegex.test(form.phone.replace(/\s/g, ''))) return 'Enter a valid phone number (10+ digits, no letters)';
    }
    if (step === 2) {
      if (form.entity_type === 'company') {
        if (!form.pan_number) return 'PAN Number is required';
        if (form.gst_number && form.gst_number.length < 15) return 'GST Number must be 15 characters (or leave empty)';
      }
      if (form.pan_number && !panRegex.test(form.pan_number.toUpperCase())) return 'PAN format: 5 letters + 4 digits + 1 letter (e.g., AAAAA1234A)';
      if (!form.registered_address) return 'Address is required';
      if (!form.bank_account_name) return 'Bank account name is required';
      if (!form.bank_account_number) return 'Bank account number is required';
      if (!form.bank_ifsc) return 'IFSC code is required';
      if (!form.bank_name) return 'Bank name is required';
    }
    if (step === 3) {
      const total = Object.values(form.team_capacity).reduce((s, v) => s + (parseInt(v) || 0), 0);
      if (total === 0) return 'Declare at least 1 team member';
    }
    if (step === 4) {
      if (!form.skills && form.tools.length === 0) return 'Add at least one skill or select a tool';
    }
    if (step === 7) {
      if (!form.nda_signed) return 'You must agree to the NDA terms';
      if (!form.nda_signature) return 'Please type your full name as digital signature';
    }
    return null;
  };

  const next = () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setStep(s => Math.min(s + 1, 8));
  };
  const prev = () => { setError(''); setStep(s => Math.max(s - 1, 0)); };

  const updateTeam = (key, val) => {
    const tc = { ...form.team_capacity, [key]: parseInt(val) || 0 };
    const total = Object.values(tc).reduce((s, v) => s + v, 0);
    setForm(p => ({ ...p, team_capacity: tc, total_team_size: total }));
  };

  const toggleTool = (tool) => {
    setForm(p => ({ ...p, tools: p.tools.includes(tool) ? p.tools.filter(t => t !== tool) : [...p.tools, tool] }));
  };

  const submit = async () => {
    if (form.password !== form.confirm_password) { setError('Passwords do not match'); return; }
    if (!form.nda_signed) { setError('NDA must be signed'); return; }
    setSubmitting(true); setError('');
    try {
      const skillsArr = typeof form.skills === 'string' ? form.skills.split(',').map(s => s.trim()).filter(Boolean) : (form.skills || []);
      const pastProjects = typeof form.past_projects === 'string' ? form.past_projects : '';
      const data = { ...form, skills: skillsArr, past_projects: pastProjects, pricing_model: form.work_type_preference, nda_ip: 'client' };
      await axios.post(`${API}/vendor/register`, data);
      setSuccess(true);
    } catch (e) { setError(e.response?.data?.detail || 'Registration failed'); }
    setSubmitting(false);
  };

  if (success) return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
      <div className="text-center animate-fade-in">
        <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
        <h1 className="text-2xl font-heading font-bold text-white">Registration Submitted!</h1>
        <p className="text-sm text-zinc-400 mt-2">Your application is under review. We'll notify you once approved.</p>
        <p className="text-xs text-zinc-600 mt-1">Status: Pending Review</p>
        <a href="/login" className="mt-6 inline-block h-9 px-6 bg-blue-600 text-white text-sm rounded-sm leading-9">Go to Login</a>
      </div>
    </div>
  );

  const inp = (label, k, type = 'text', placeholder = '', required = false, mono = false) => (
    <div key={k}>
      <label className="text-[9px] text-zinc-500 uppercase tracking-wider">{label} {required && <span className="text-red-400">*</span>}</label>
      <input type={type} value={form[k] || ''} onChange={e => u(k, e.target.value)}
        className={`w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white focus:ring-1 focus:ring-blue-500 focus:outline-none ${mono ? 'font-mono' : ''}`}
        placeholder={placeholder} />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4" data-testid="vendor-register">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-blue-600 rounded-sm flex items-center justify-center"><Monitor className="w-4 h-4 text-white" /></div>
            <span className="text-xl font-heading font-bold text-white">VawworldOS</span>
          </div>
          <p className="text-xs text-zinc-500 uppercase tracking-wider">Vendor Producer Registration</p>
        </div>

        {/* Progress Bar */}
        <div className="flex items-center gap-1 mb-6">
          {STEPS.map((s, i) => (
            <div key={i} className="flex-1">
              <div className={`h-1 rounded-full transition-all ${i <= step ? 'bg-blue-500' : 'bg-zinc-800'}`} />
              <p className={`text-[7px] mt-1 text-center ${i === step ? 'text-blue-400' : 'text-zinc-700'}`}>{s}</p>
            </div>
          ))}
        </div>

        {/* Form Card */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
          <h2 className="text-sm font-heading font-semibold text-white mb-4">Step {step + 1}: {STEPS[step]}</h2>

          {/* STEP 0: Entity Type — Company Only (Individual uses /freelancer/register) */}
          {step === 0 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <button onClick={() => { u('entity_type', 'company'); next(); }}
                  className={`p-6 border rounded-sm text-center transition-all ${form.entity_type === 'company' ? 'border-blue-500 bg-blue-500/10' : 'border-zinc-800 hover:border-zinc-600'}`}>
                  <Building2 className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                  <p className="text-sm text-white font-medium">Company / Studio</p>
                  <p className="text-[10px] text-zinc-500 mt-1">Registered business entity with team</p>
                </button>
              </div>
              <p className="text-center text-[10px] text-zinc-600">
                Are you an individual freelancer? <a href="/freelancer/register" className="text-amber-400 hover:underline">Register here instead</a>
              </p>
            </div>
          )}

          {/* STEP 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-3">
              {form.entity_type === 'company' ? inp('Company Name', 'company_name', 'text', 'Your Studio Pvt Ltd', true) : inp('Full Name', 'full_name', 'text', 'Your full name', true)}
              {inp('Email', 'email', 'email', 'vendor@company.com', true, false)}
              <div className="grid grid-cols-2 gap-3">
                {inp('Password', 'password', 'password', 'Min 6 characters', true, false)}
                {inp('Confirm Password', 'confirm_password', 'password', 'Repeat', true, false)}
              </div>
              {inp('Phone Number', 'phone', 'text', '+91-9876543210', true, false)}
            </div>
          )}

          {/* STEP 2: Entity Details */}
          {step === 2 && (
            <div className="space-y-3">
              {form.entity_type === 'company' ? (<>
                {inp('Company Legal Name', 'company_name', 'text', '', true, false)}
                <div className="grid grid-cols-2 gap-3">
                  {inp('GST Number (optional)', 'gst_number', 'text', '22AAAAA0000A1Z5', false, true)}
                  {inp('Company PAN', 'pan_number', 'text', 'AAAAA1234A', true, true)}
                </div>
                {inp('CIN (optional)', 'cin_number', 'text', 'U12345MH2020PTC123456', false, true)}
                {inp('Authorized Person Name', 'authorized_person', 'text', '', true, false)}
                {inp('Auth Person PAN', 'authorized_person_pan', 'text', '', false, true)}
              </>) : (<>
                {inp('Aadhar Number', 'aadhar_number', 'text', '1234-5678-9012', false, true)}
                {inp('PAN Number', 'pan_number', 'text', 'AAAAA1234A', true, true)}
              </>)}
              {inp('Registered Address', 'registered_address', 'text', 'Full address', true, false)}
              {inp('Pincode', 'pincode', 'text', '400001', false, false)}
              <div className="grid grid-cols-2 gap-3">
                {inp('Bank Account Name', 'bank_account_name', 'text', '', true, false)}
                {inp('Account Number', 'bank_account_number', 'text', '', true, true)}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {inp('IFSC Code', 'bank_ifsc', 'text', 'SBIN0001234', true, true)}
                {inp('Bank Name', 'bank_name', 'text', 'State Bank of India', true, false)}
              </div>
            </div>
          )}

          {/* STEP 3: Team Capacity */}
          {step === 3 && (
            <div className="space-y-3">
              <p className="text-[10px] text-zinc-500 mb-2">Declare your team strength by department:</p>
              <div className="grid grid-cols-2 gap-3">
                {TEAM_ROLES.map(r => (
                  <div key={r.key} className="flex items-center gap-2">
                    <label className="text-[10px] text-zinc-400 flex-1">{r.label}</label>
                    <input type="number" min="0" value={form.team_capacity[r.key] || ''} onChange={e => updateTeam(r.key, e.target.value)}
                      className="w-16 h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-white text-center focus:outline-none" />
                  </div>
                ))}
              </div>
              <div className="pt-3 border-t border-zinc-800 flex items-center justify-between">
                <span className="text-xs text-zinc-400">Total Team Size:</span>
                <span className="text-lg font-heading font-bold text-blue-400">{form.total_team_size}</span>
              </div>
              {inp('Peak Capacity (shots/week)', 'peak_capacity', 'text', 'e.g., 50 shots/week', false, false)}
            </div>
          )}

          {/* STEP 4: Skills & Tools */}
          {step === 4 && (
            <div className="space-y-4">
              {inp('Skills (comma separated)', 'skills', 'text', '2D Animation, 3D Modeling, Compositing, VFX', false, false)}
              <div>
                <label className="text-[9px] text-zinc-500 uppercase tracking-wider mb-2 block">Tools Used</label>
                <div className="flex flex-wrap gap-2">
                  {TOOLS_LIST.map(t => (
                    <button key={t} onClick={() => toggleTool(t)}
                      className={`px-3 py-1.5 rounded-sm text-[10px] border transition-all ${form.tools.includes(t) ? 'bg-blue-500/20 border-blue-500/50 text-blue-400' : 'bg-zinc-800/50 border-zinc-800 text-zinc-500 hover:border-zinc-600'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* STEP 5: Experience */}
          {step === 5 && (
            <div className="space-y-3">
              {inp('Years of Experience', 'years_experience', 'number', '5', false, false)}
              <div>
                <label className="text-[9px] text-zinc-500 uppercase">Past Projects (one per line)</label>
                <textarea value={form.past_projects} onChange={e => u('past_projects', e.target.value)} rows={4}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-xs text-white focus:outline-none"
                  placeholder="Project Name - Client - Year&#10;Dragon Tales S02 - StreamMax - 2025&#10;Cyber Samurai - Anime Global - 2024" />
              </div>
              {inp('Showreel URL', 'showreel_url', 'text', 'https://vimeo.com/your-reel', false, false)}
              <div>
                <label className="text-[9px] text-zinc-500 uppercase">Preferred Work Type</label>
                <div className="flex gap-2 mt-1">
                  {['per_shot', 'per_minute', 'full_project'].map(wt => (
                    <button key={wt} onClick={() => u('work_type_preference', wt)}
                      className={`px-3 py-1.5 rounded-sm text-[10px] border ${form.work_type_preference === wt ? 'bg-blue-500/20 border-blue-500/50 text-blue-400' : 'bg-zinc-800/50 border-zinc-800 text-zinc-500'}`}>
                      {wt.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>
              {inp('Portfolio / Website URL', 'portfolio_url', 'text', 'https://yourstudio.com', false, false)}
            </div>
          )}

          {/* STEP 6: Documents */}
          {step === 6 && (
            <div className="space-y-2">
              <p className="text-[10px] text-zinc-500 mb-3">Upload required documents. These will be verified by our team.</p>
              {[
                { l: 'PAN Card', r: true },
                { l: 'Aadhar Card', r: form.entity_type === 'individual' },
                { l: 'Bank Proof (Cancelled Cheque / Passbook)', r: true },
                ...(form.entity_type === 'company' ? [{ l: 'Company Registration Certificate', r: true }, { l: 'GST Certificate', r: true }] : []),
              ].map(d => (
                <div key={d.l} className="flex items-center justify-between bg-zinc-800/30 border border-zinc-800/50 rounded-sm px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-zinc-600" />
                    <span className="text-xs text-zinc-300">{d.l} {d.r && <span className="text-red-400">*</span>}</span>
                  </div>
                  <span className="text-[9px] text-amber-400">Upload after registration via Profile</span>
                </div>
              ))}
              <p className="text-xs text-amber-400 mt-2 font-medium">Documents can be uploaded after registration from your Profile dashboard. Complete registration first.</p>
            </div>
          )}

          {/* STEP 7: NDA */}
          {step === 7 && (
            <div className="space-y-4">
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4 max-h-48 overflow-y-auto text-[10px] text-zinc-400 leading-relaxed">
                <p className="font-bold text-zinc-300 mb-2">NON-DISCLOSURE AGREEMENT (NDA)</p>
                <p>This Non-Disclosure Agreement is entered into between VawWorld Media Pvt Ltd ("Company") and the Vendor ("Recipient").</p>
                <p className="mt-2"><strong>1. Confidential Information:</strong> All project data, scripts, character designs, storyboards, animation files, voice recordings, and any production materials shared by VawWorld Media.</p>
                <p className="mt-2"><strong>2. Obligations:</strong> The Recipient shall not disclose, copy, distribute, or use Confidential Information for any purpose other than the assigned project work.</p>
                <p className="mt-2"><strong>3. Duration:</strong> This NDA remains in effect for 5 years from the date of signing.</p>
                <p className="mt-2"><strong>4. Penalties:</strong> Breach of this NDA may result in legal action and damages as per Indian law.</p>
                <p className="mt-2"><strong>5. Return of Materials:</strong> Upon project completion or termination, all materials must be returned or destroyed.</p>
                <p className="mt-2"><strong>6. Jurisdiction:</strong> Courts of Mumbai, Maharashtra shall have exclusive jurisdiction.</p>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.nda_signed} onChange={e => u('nda_signed', e.target.checked)}
                  className="w-4 h-4 bg-zinc-800 border-zinc-700 rounded text-blue-500 focus:ring-blue-500" />
                <span className="text-xs text-zinc-300">I have read and agree to the NDA terms</span>
              </label>
              <div>
                <label className="text-[9px] text-zinc-500 uppercase">Digital Signature (Type your full name)</label>
                <input value={form.nda_signature} onChange={e => u('nda_signature', e.target.value)}
                  className="w-full h-10 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-lg text-white italic font-serif focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Type your full legal name as signature" />
                {form.nda_signature && <p className="text-[9px] text-zinc-600 mt-1">Signed as: "{form.nda_signature}" at {new Date().toLocaleString()}</p>}
              </div>
            </div>
          )}

          {/* STEP 8: Review */}
          {step === 8 && (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              <div className="grid grid-cols-2 gap-3 text-xs">
                {[
                  ['Entity Type', form.entity_type],
                  ['Name', form.full_name || form.company_name],
                  ['Email', form.email],
                  ['Phone', form.phone],
                  ['PAN', form.pan_number],
                  ...(form.entity_type === 'company' ? [['GST', form.gst_number], ['CIN', form.cin_number]] : [['Aadhar', form.aadhar_number]]),
                  ['Bank', `${form.bank_name} - ${form.bank_account_number}`],
                  ['IFSC', form.bank_ifsc],
                  ['Team Size', form.total_team_size],
                  ['Peak Capacity', form.peak_capacity],
                  ['Experience', `${form.years_experience} years`],
                  ['Work Type', form.work_type_preference?.replace(/_/g, ' ')],
                  ['Tools', form.tools.join(', ')],
                  ['NDA Signed', form.nda_signed ? 'Yes' : 'NO'],
                ].map(([l, v], i) => (
                  <div key={i} className="bg-zinc-800/30 rounded-sm px-3 py-2">
                    <p className="text-[9px] text-zinc-600 uppercase">{l}</p>
                    <p className={`text-zinc-300 ${l === 'NDA Signed' && !form.nda_signed ? 'text-red-400' : ''}`}>{v || '-'}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {error && <p className="text-red-400 text-xs mt-3 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{error}</p>}

          {/* Navigation */}
          <div className="flex justify-between mt-6 pt-4 border-t border-zinc-800">
            {step > 0 ? (
              <button onClick={prev} className="h-9 px-5 bg-zinc-800 text-zinc-300 text-xs rounded-sm flex items-center gap-1.5">
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </button>
            ) : <div />}
            {step < 8 ? (
              <button onClick={next} className="h-9 px-5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-sm flex items-center gap-1.5">
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting || !form.nda_signed}
                className="h-9 px-6 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium rounded-sm disabled:opacity-40 flex items-center gap-1.5">
                {submitting ? 'Submitting...' : 'Submit Registration'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
